/**
 * API Expose live event feed SSE honesty.
 *
 * GET /cp/services/api-observe/events/stream is a real SSE door.
 * EventSource does not error on an open heartbeat (or hung CONNECTING)
 * stream, and the old hook retried onerror forever — so the feed stayed
 * on “Reconnecting… (feed not live)” / “Waiting for events…”.
 *
 * After the wait: timeout copy + retry, or “No live events”.
 * Do not invent public_agent_execute or ingest.raw events.
 */

export const EXPOSE_LIVE_FEED_WAIT_MS = 15_000;

export const EXPOSE_LIVE_FEED_SSE_PATH = '/cp/services/api-observe/events/stream';

export type ExposeLiveFeedPhase =
  | 'paused'
  | 'no_door'
  | 'waiting'
  | 'live'
  | 'empty'
  | 'timeout'
  | 'error';

export function nextExposeLiveFeedPhase(input: {
  paused: boolean;
  hasSseUrl: boolean;
  eventCount: number;
  streamOpen: boolean;
  streamError: boolean;
  timedOut: boolean;
}): ExposeLiveFeedPhase {
  if (input.paused) return 'paused';
  if (!input.hasSseUrl) return 'no_door';
  if (input.eventCount > 0) return 'live';
  if (input.streamError) return 'error';
  if (input.timedOut) return input.streamOpen ? 'empty' : 'timeout';
  return 'waiting';
}

export function exposeLiveFeedStatusLabel(phase: ExposeLiveFeedPhase): string {
  switch (phase) {
    case 'live':
      return '● Live';
    case 'waiting':
      return '○ Connecting… (feed not live yet)';
    case 'empty':
      return '○ No live events';
    case 'timeout':
    case 'error':
      return '○ Reconnect failed (feed not live)';
    case 'paused':
      return '○ Paused';
    case 'no_door':
      return '○ Offline';
  }
}

export function exposeLiveFeedEmptyCopy(phase: ExposeLiveFeedPhase): {
  title: string;
  body: string;
  retry: boolean;
} {
  switch (phase) {
    case 'no_door':
      return {
        title: 'No live events',
        body: 'This panel has no signed-in SSE stream. It does not invent public_agent_execute or ingest.raw events.',
        retry: false,
      };
    case 'paused':
      return {
        title: 'Streaming is paused',
        body: 'Turn Streaming on to wait for the live SSE door. This feed does not invent events.',
        retry: false,
      };
    case 'empty':
      return {
        title: 'No live events',
        body:
          'The live SSE stream is open and no observe events arrived in this wait. ' +
          'This feed does not invent public_agent_execute or ingest.raw events.',
        retry: true,
      };
    case 'timeout':
      return {
        title: 'Live stream timed out',
        body:
          'No SSE events arrived before the wait ended. Retry, or this feed stays empty. ' +
          'It does not invent public_agent_execute or ingest.raw events.',
        retry: true,
      };
    case 'error':
      return {
        title: 'Reconnect failed',
        body:
          'The SSE door failed to stay live. Retry. This feed does not invent public_agent_execute or ingest.raw events.',
        retry: true,
      };
    case 'waiting':
      return {
        title: 'Waiting for events…',
        body: '',
        retry: false,
      };
    case 'live':
      return { title: '', body: '', retry: false };
  }
}
